import {GeneratedType} from "@shareledgerjs/signing";
import Long from "long";

import {BaseClient} from "../../baseclient";
import {MsgExecuteContractEncodeObject, MsgInstantiateContractEncodeObject, WasmTxExtension} from "../wasm";
import {Cw20BaseExecuteMsg, Expiration, InstantiateMsg} from "./types/Cw20Base.types";

export function createTokenTypes(): ReadonlyArray<[string, GeneratedType]> {
  return [];
}

export interface TokenTxExtensionMethods {
  instantiate(owner: string, codeId: Long, label: string, msg: InstantiateMsg): MsgInstantiateContractEncodeObject;
  mint(contract: string, owner: string, recipient: string, amount: string): MsgExecuteContractEncodeObject;
  transfer(contract: string, owner: string, recipient: string, amount: string): MsgExecuteContractEncodeObject;
  transferFrom(contract: string, sender: string, owner: string, recipient: string, amount: string): MsgExecuteContractEncodeObject;
  burn(contract: string, owner: string, amount: string): MsgExecuteContractEncodeObject;
  burnFrom(contract: string, sender: string, owner: string, amount: string): MsgExecuteContractEncodeObject;
  increaseAllowance(contract: string, owner: string, spender: string, amount: string, expires?: Expiration): MsgExecuteContractEncodeObject;
  decreaseAllowance(contract: string, owner: string, spender: string, amount: string, expires?: Expiration): MsgExecuteContractEncodeObject;
  setMinter(contract: string, owner: string, recipient: string): MsgExecuteContractEncodeObject;
}

export interface TokenTxExtension {
  readonly token: TokenTxExtensionMethods;
}

export function TokenTxExtension<T extends {new (...args: any[]): BaseClient & TokenTxExtension & WasmTxExtension}>(constructor: T): T {
  return class extends constructor {
    override get token() {
      return {
        ...super["token"],
        instantiate: (owner: string, codeId: Long, label: string, msg: InstantiateMsg) => {
          return this.wasm.instantiateContract(owner, owner, codeId, label, msg, []);
        },
        mint: (contract: string, owner: string, recipient: string, amount: string) => {
          return this.wasm.executeContract<Cw20BaseExecuteMsg>(
            owner,
            contract,
            {
              mint: {
                recipient,
                amount
              }
            },
            []
          );
        },
        transfer: (contract: string, owner: string, recipient: string, amount: string) => {
          return this.wasm.executeContract<Cw20BaseExecuteMsg>(
            owner,
            contract,
            {
              transfer: {
                amount,
                recipient
              }
            },
            []
          );
        },
        transferFrom: (contract: string, sender: string, owner: string, recipient: string, amount: string) => {
          return this.wasm.executeContract<Cw20BaseExecuteMsg>(
            sender,
            contract,
            {
              transfer_from: {
                owner,
                amount,
                recipient
              }
            },
            []
          );
        },
        setMinter: (contract: string, owner: string, recipient: string) => {
          return this.wasm.executeContract<Cw20BaseExecuteMsg>(
            owner,
            contract,
            {
              update_minter: {
                new_minter: recipient
              }
            },
            []
          );
        },
        increaseAllowance: (contract: string, owner: string, spender: string, amount: string, expires?: Expiration) => {
          return this.wasm.executeContract<Cw20BaseExecuteMsg>(
            owner,
            contract,
            {
              increase_allowance: {
                amount,
                spender,
                expires
              }
            },
            []
          );
        },
        decreaseAllowance: (contract: string, owner: string, spender: string, amount: string, expires?: Expiration) => {
          return this.wasm.executeContract<Cw20BaseExecuteMsg>(
            owner,
            contract,
            {
              decrease_allowance: {
                amount,
                spender,
                expires
              }
            },
            []
          );
        },
        burn: (contract: string, owner: string, amount: string) => {
          return this.wasm.executeContract<Cw20BaseExecuteMsg>(
            owner,
            contract,
            {
              burn: {
                amount
              }
            },
            []
          );
        },
        burnFrom: (contract: string, sender: string, owner: string, amount: string) => {
          return this.wasm.executeContract<Cw20BaseExecuteMsg>(
            sender,
            contract,
            {
              burn_from: {
                owner,
                amount
              }
            },
            []
          );
        }
      };
    }
  };
}
